import { prisma } from "client";

/**
 * Get privacy policy for a white label with all translations
 */
export const getPrivacyPolicy = async (whiteLabelId: string) => {
  return await prisma.privacyPolicy.findUnique({
    where: { whiteLabelId },
    include: {
      translations: {
        include: {
          language: true,
        },
      },
    },
  });
};

/**
 * Get privacy policy for a specific language
 */
export const getPrivacyPolicyByLanguage = async (
  whiteLabelId: string,
  languageId: string
) => {
  return await prisma.privacyPolicyTranslations.findFirst({
    where: {
      privacyPolicy: {
        whiteLabelId,
      },
      languageId,
    },
    include: {
      language: true,
      privacyPolicy: true,
    },
  });
};

/**
 * Create or update privacy policy with translations
 * @param whiteLabelId - White Label ID
 * @param translations - Array of translations { languageId: string, content: string }
 */
export const upsertPrivacyPolicy = async (
  whiteLabelId: string,
  translations: Array<{ languageId: string; content: string }>
) => {
  const startTime = Date.now();

  try {
    // Find existing privacy policy
    const existing = await prisma.privacyPolicy.findUnique({
      where: { whiteLabelId },
      include: { translations: true },
    });

    if (existing) {
      // Update existing translations and create new ones
      const translationPromises = translations.map(async (translation) => {
        const existingTranslation = existing.translations.find(
          (t) => t.languageId === translation.languageId
        );

        if (existingTranslation) {
          // Update existing translation
          return prisma.privacyPolicyTranslations.update({
            where: { id: existingTranslation.id },
            data: { content: translation.content },
          });
        } else {
          // Create new translation
          return prisma.privacyPolicyTranslations.create({
            data: {
              privacyPolicyId: existing.id,
              languageId: translation.languageId,
              content: translation.content,
            },
          });
        }
      });

      await Promise.all(translationPromises);

      // Return updated privacy policy with translations
      return await prisma.privacyPolicy.findUnique({
        where: { whiteLabelId },
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      });
    } else {
      // Create new privacy policy with translations
      return await prisma.privacyPolicy.create({
        data: {
          whiteLabelId,
          translations: {
            create: translations.map((t) => ({
              languageId: t.languageId,
              content: t.content,
            })),
          },
        },
        include: {
          translations: {
            include: {
              language: true,
            },
          },
        },
      });
    }
  } catch (error) {
    console.error(
      `Database: Operation failed after ${Date.now() - startTime}ms:`,
      error
    );
    throw error;
  }
};

/**
 * Delete privacy policy (will cascade delete all translations)
 */
export const deletePrivacyPolicy = async (whiteLabelId: string) => {
  return await prisma.privacyPolicy.delete({
    where: { whiteLabelId },
  });
};

/**
 * Delete a specific translation
 */
export const deletePrivacyPolicyTranslation = async (
  translationId: string
) => {
  return await prisma.privacyPolicyTranslations.delete({
    where: { id: translationId },
  });
};

